import type React from 'react';
import { CODE_FONT, hangingIndentStyle } from './codeBlockStyles';

const LINE_HEIGHT = '20px';
const LINE_NUMBER_WIDTH = '40px';
const MARKER_WIDTH = '14px';

/** How a diff marks one line up. Absent everywhere else, where a line is just a line. */
export interface CodeLineDecoration {
    /** Tint for the whole row. */
    background?: string;
    /** One character in the gutter, ahead of the line number. */
    marker?: string;
    /** Overrides the marker's colour. Falls back to the usual line-number grey. */
    markerColor?: string;
    /** Reserves the gutter on an unmarked row, so marked and unmarked rows stay aligned. */
    reserveMarker?: boolean;
    /** Overrides the line's own text colour, e.g. to de-emphasise an unchanged diff row. */
    textColor?: string;
    /** Strikes through the line's text, e.g. a diff row a change removed. */
    strikethrough?: boolean;
    /** Rendered as its own row above this one — what a diff says in place of omitted lines. */
    separatorAbove?: string;
    /**
     * The number to show, overriding the row's position. A diff numbers each line from the
     * text it came from, which is not where it sits in the merged sequence.
     */
    lineNumber?: number;
}

export interface CodeLineProps {
    line: string;
    lineNumber?: number;
    wrap: boolean;
    decoration?: CodeLineDecoration;
    children: React.ReactNode;
}

export const CodeLine: React.FC<CodeLineProps> = ({
    line,
    lineNumber,
    wrap,
    decoration,
    children,
}) => {
    const showMarker = decoration?.marker !== undefined || decoration?.reserveMarker === true;

    const row = (
        <span
            className="flex items-start"
            style={{
                minHeight: LINE_HEIGHT,
                background: decoration?.background,
                // Fills the wrapper: `auto` resolves to the visible width inside the block's
                // scroller, cutting a long line's background off at its right edge.
                width: '100%',
            }}
        >
            {showMarker && (
                <span
                    aria-hidden="true"
                    className="select-none flex-shrink-0"
                    style={{
                        ...CODE_FONT,
                        textAlign: 'center',
                        color: decoration?.markerColor ?? 'var(--malachite-codeblock-line-number)',
                        width: MARKER_WIDTH,
                    }}
                >
                    {decoration?.marker ?? ''}
                </span>
            )}
            {lineNumber !== undefined && (
                <span
                    aria-hidden="true"
                    className="select-none pr-4 flex-shrink-0"
                    style={{
                        ...CODE_FONT,
                        textAlign: 'right',
                        color: 'var(--malachite-codeblock-line-number)',
                        width: LINE_NUMBER_WIDTH,
                    }}
                >
                    {lineNumber}
                </span>
            )}
            <span
                className="flex-1 text-left"
                style={{
                    ...CODE_FONT,
                    whiteSpace: wrap ? 'pre-wrap' : 'pre',
                    overflowWrap: wrap ? 'anywhere' : undefined,
                    // Only overrides CODE_FONT's color when a decoration asks for it.
                    ...(decoration?.textColor ? { color: decoration.textColor } : undefined),
                    textDecoration: decoration?.strikethrough ? 'line-through' : undefined,
                    ...(wrap ? hangingIndentStyle(line) : undefined),
                }}
            >
                {children}
            </span>
        </span>
    );

    if (decoration?.separatorAbove === undefined) {
        return row;
    }

    return (
        <>
            <span
                className="flex items-center select-none pl-2"
                style={{
                    ...CODE_FONT,
                    minHeight: LINE_HEIGHT,
                    color: 'var(--malachite-codeblock-comment)',
                    borderTop: '1px solid var(--malachite-codeblock-border)',
                    borderBottom: '1px solid var(--malachite-codeblock-border)',
                }}
            >
                {decoration.separatorAbove}
            </span>
            {row}
        </>
    );
};
